import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { RouteSpinner } from '@/app/router/RouteSpinner';
import { AppStatusPage } from '@/features/appStatus/pages/AppStatusPage';
import { useAppStatusQuery } from '@/features/appStatus/api/useAppStatusQuery';
import { BillboardDetailPage } from '@/features/billboards/pages/BillboardDetailPage';
import { BillboardsPage } from '@/features/billboards/pages/BillboardsPage';
import { PolicySlugPage } from '@/features/policies/pages/PolicySlugPage';
import { routes } from '@/shared/config/routes';

const PartnerKycRegistrationPage = lazy(() =>
  import('@/features/partner/pages/PartnerKycRegistrationPage').then(
    (module) => ({
      default: module.PartnerKycRegistrationPage,
    }),
  ),
);

const TutorialPage = lazy(() =>
  import('@/features/tutorial/pages/TutorialPage').then((module) => ({
    default: module.TutorialPage,
  })),
);

const LandingPage = lazy(() =>
  import('@/features/landing/pages/LandingPage').then((module) => ({
    default: module.LandingPage,
  })),
);

function LandingRoute() {
  return (
    <Suspense fallback={<RouteSpinner />}>
      <LandingPage />
    </Suspense>
  );
}

function AppStatusRoute() {
  const { data, isLoading, isError } = useAppStatusQuery();
  const config = data?.data;

  if (isLoading) return <RouteSpinner />;
  if (isError || !config) {
    return (
      <AppStatusPage
        config={{
          showPopup: true,
          title: 'Screenista',
          message: 'Unable to load status right now. Please try again shortly.',
          type: 'issue',
          isCloseable: true,
        }}
      />
    );
  }

  return <AppStatusPage config={config} />;
}

export const publicRoutes: RouteObject[] = [
  { path: routes.home, element: <LandingRoute /> },
  { path: '/v2', element: <Navigate to={routes.home} replace /> },
  { path: '/v2/*', element: <Navigate to={routes.home} replace /> },
  { path: '/billboard/:id', element: <BillboardDetailPage /> },
  { path: routes.publicBillboards, element: <BillboardsPage /> },
  { path: routes.status, element: <AppStatusRoute /> },
  { path: routes.maintenance, element: <AppStatusRoute /> },
  { path: '/pages/:slug', element: <PolicySlugPage /> },
  {
    path: routes.tutorial,
    element: (
      <Suspense fallback={<RouteSpinner />}>
        <TutorialPage />
      </Suspense>
    ),
  },
  {
    path: routes.partnerKycRegistration,
    element: (
      <Suspense fallback={<RouteSpinner />}>
        <PartnerKycRegistrationPage />
      </Suspense>
    ),
  },
  {
    path: '/kyc/partner-registration',
    element: <Navigate to={routes.partnerKycRegistration} replace />,
  },
];
